import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { Route, Switch, useLocation } from 'react-router-dom'
import { useMediaQuery, useTheme } from '@mui/material'
import { encode } from 'js-base64'
import _ from 'lodash'

/** COMPONENT */
import routes from '../../router'
import { resolveRoute } from '../../routes/resolveRoute'
import Header from '../header/header'
import Sidebar from '../sidebar/sidebar'
import PageNotFound from '../../pages/pageNotFound/pageNotFound'
import HeirExpiredAlert from './HeirExpiredAlert'

/** UTILS */
import { getPermissionMenus, getPermissions } from '../../utils/app.utils'
import { STORAGE_KEY } from '../../utils/enum'

/** STYLE */
import { ContainerComponent, ContainerMenu, ContainerContent } from './appShell.style'

/**
 * แบนเมนู 3 ชั้นจาก PERMISSION ให้เป็นลิสต์เดียว (ยกมาจาก handleLoadMenus เดิมใน App.tsx)
 * เดิมเก็บ allMenusKey คู่กันด้วยแต่ไม่มีใครอ่าน จึงตัดทิ้ง
 */
const flattenMenuPermissions = (menus: any) => {
  const menuPermission: any[] = []
  _.map(menus, (menu: any) => {
    menuPermission.push(_.omit(menu, 'menuSeconds'))
    _.map(menu.menuSeconds, (menu2: any) => {
      menuPermission.push(_.omit(menu2, 'menuThirds'))
      _.map(menu2.menuThirds, (menu3: any) => menuPermission.push(menu3))
    })
  })
  return menuPermission
}

/**
 * เปลือกของหน้าที่ล็อกอินแล้ว — Header/Sidebar อยู่ตรงนี้ตัวเดียว mount ครั้งเดียวตลอดเซสชัน
 * เดิมทั้งคู่ถูกใส่ไว้ใน element ของ <Route> ทุกตัว (ราว 78 ชุด) จึง unmount/remount ทุกครั้งที่เปลี่ยนหน้า
 * และ Sidebar ยิง API นับ badge ใหม่ทุกครั้งตามไปด้วย
 */
export default function AppShell() {
  const theme = useTheme()
  const matches = useMediaQuery(theme.breakpoints.down('sm'))
  const { pathname } = useLocation()

  const currentRoute = useMemo(() => resolveRoute(pathname), [pathname])
  const appBar: boolean = currentRoute?.appBar ?? true
  const textName = currentRoute?.textName
  const routeKey = currentRoute?.key

  /** สิทธิ์ทั้งชุดไม่เปลี่ยนระหว่างเซสชัน อ่าน+แบนครั้งเดียว
   *  ต่างจากเดิมที่ทำใน useEffect ทำให้ render รอบแรกยังเป็น [] */
  const menuPermissions = useMemo(() => flattenMenuPermissions(getPermissionMenus()), [])

  const activeMenu = useMemo(
    () =>
      _.find(menuPermissions, { menuFirstKey: routeKey }) ||
      _.find(menuPermissions, { menuSecondKey: routeKey }) ||
      _.find(menuPermissions, { menuThirdKey: routeKey }),
    [menuPermissions, routeKey]
  )

  /**
   * เดิม PrivateRoute เขียน activeMenu ลง localStorage ระหว่าง render phase
   * ย้ายมาไว้ใน effect แล้วกั้นไม่ให้หน้าเรนเดอร์จนกว่าจะเขียนเสร็จ เพราะ utils/permission.utils
   * อ่าน localStorage ตอน render ของแต่ละหน้า (isView/isCreate/isApporve) — ถ้าไม่กั้น
   * รอบแรกของทุกหน้าจะอ่านสิทธิ์ของหน้าก่อนหน้า ปุ่มจะจางผิด
   *
   * useLayoutEffect ไม่ใช่ useEffect เพื่อให้เขียน + setState เสร็จก่อน paint ผู้ใช้จึงไม่เห็นจอว่างแวบหนึ่ง
   */
  const [permissionSyncedPath, setPermissionSyncedPath] = useState<string | null>(null)
  useLayoutEffect(() => {
    const permissions = { ...getPermissions(), activeMenu }
    localStorage.setItem(STORAGE_KEY.PERMISSION, encode(JSON.stringify(permissions)))
    setPermissionSyncedPath(pathname)
  }, [pathname, activeMenu])

  const isPermissionReady = permissionSyncedPath === pathname

  /** full reload เดิมรีเซ็ต scroll ให้ฟรี พอเปลี่ยนหน้าแบบ SPA ต้องทำเอง
   *  ตัวที่ scroll จริงคือ .container-content (overflow: auto) ไม่ใช่ window */
  useLayoutEffect(() => {
    document.querySelector('.container-content')?.scrollTo(0, 0)
  }, [pathname])

  /** เมนูฝั่งจอเล็ก: state อยู่ตรงนี้เพราะทั้ง Header (ปุ่ม hamburger) และ Sidebar (ตัวเมนู) ต้องเห็นค่าเดียวกัน
   *  เดิม Header ไปเติม class ใส่ DOM เอง ซึ่งชนกับ className ที่ Sidebar คุมผ่าน React */
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [pathname])

  return (
    <>
      <Header menuName={textName} menuOpen={mobileMenuOpen} onToggleMenu={() => setMobileMenuOpen((open) => !open)} />
      <ContainerComponent className={`div-component d-flex ${appBar ? 'flex-row' : 'flex-column no-appBar'}`}>
        {appBar && <ContainerMenu className={'container-menu'}><Sidebar activeMenu={activeMenu} mobileOpen={mobileMenuOpen} /></ContainerMenu>}
        <ContainerContent className={`container-content pb-5`} appBar={appBar}>
          {isPermissionReady && (
            <Switch>
              {routes.map(({ path, component }: any, idx: number) => (
                /** children form เหมือนเดิม ไม่ส่ง match/location/history เป็น props — หน้าเพจใช้ useParams เอง */
                <Route exact path={path} key={`${path || 'catch-all'}-${idx}`}>
                  {component && React.createElement(component)}
                </Route>
              ))}
              <Route key="PageNotFound" component={PageNotFound} />
            </Switch>
          )}

          {
            matches && (
              <div style={{
                paddingBottom: 100
              }}
              />
            )
          }
        </ContainerContent>
      </ContainerComponent>
      <HeirExpiredAlert />
    </>
  )
}
